import React, { useEffect, useRef, useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  FormLabel,
  Alert,
  LinearProgress,
  Chip,
  Stack,
  Divider,
} from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import CancelOutlinedIcon from '@mui/icons-material/CancelOutlined';
import TimerIcon from '@mui/icons-material/Timer';
import {
  ActiveQuizDTO,
  QuizSubmissionResultDTO,
  submitQuizAnswers,
} from '../../services/studentSessionService';

interface Props {
  quiz: ActiveQuizDTO;
  onDismiss: () => void;
}

export default function PlaygroundQuiz({ quiz, onDismiss }: Props) {
  const [selectedOptions, setSelectedOptions] = useState<Record<number, number>>({});
  const [result, setResult] = useState<QuizSubmissionResultDTO | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [timeLeft, setTimeLeft] = useState(quiz.remainingSeconds);
  const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    setTimeLeft(quiz.remainingSeconds);
  }, [quiz.quizSessionId, quiz.remainingSeconds]);

  useEffect(() => {
    if (result) return;
    intervalRef.current = setInterval(() => {
      setTimeLeft(t => Math.max(0, t - 1));
    }, 1000);
    return () => { if (intervalRef.current) clearInterval(intervalRef.current); };
  }, [result]);

  function handleSelect(questionId: number, optionId: number) {
    if (result) return;
    setSelectedOptions(prev => ({ ...prev, [questionId]: optionId }));
  }

  async function handleSubmit() {
    const unanswered = quiz.questions.filter(q => !selectedOptions[q.id]);
    if (unanswered.length > 0) {
      setError(`Please answer all questions (${unanswered.length} remaining).`);
      return;
    }
    setError(null);
    setSubmitting(true);
    try {
      const answers = Object.entries(selectedOptions).map(([qId, optId]) => ({
        questionId: Number(qId),
        selectedOptionId: optId,
      }));
      const res = await submitQuizAnswers(quiz.quizSessionId, answers);
      setResult(res);
      if (intervalRef.current) clearInterval(intervalRef.current);
    } catch (err: any) {
      if (err?.response?.status === 409) {
        setError(err.response.data?.message ?? 'Quiz already submitted or timer expired.');
      } else {
        setError('Failed to submit. Please try again.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  const progress = (timeLeft / quiz.timerSeconds) * 100;
  const timerColor = timeLeft <= 10 ? 'error' : timeLeft <= 30 ? 'warning' : 'primary';

  if (result) {
    return (
      <Box sx={{ p: 2 }}>
        <Typography variant="h6" fontWeight={600} sx={{ mb: 1 }}>
          Quiz Results — {result.score}/{result.totalQuestions}
        </Typography>
        <Stack spacing={1.5}>
          {quiz.questions.map(q => {
            const answerResult = result.answers.find(a => a.questionId === q.id);
            const chosen = answerResult?.selectedOptionId;
            return (
              <Box key={q.id}>
                <Typography variant="body2" fontWeight={500} sx={{ mb: 0.5 }}>
                  {q.text}
                </Typography>
                {q.options.map(opt => {
                  const isChosen = opt.id === chosen;
                  const isCorrect = answerResult?.correct && isChosen;
                  const isWrong = !answerResult?.correct && isChosen;
                  return (
                    <Box
                      key={opt.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        px: 1,
                        py: 0.25,
                        borderRadius: 1,
                        bgcolor: isCorrect ? 'success.50' : isWrong ? 'error.50' : 'transparent',
                        border: isChosen ? '1px solid' : '1px solid transparent',
                        borderColor: isCorrect ? 'success.light' : isWrong ? 'error.light' : 'transparent',
                      }}
                    >
                      {isCorrect && <CheckCircleOutlineIcon color="success" fontSize="small" />}
                      {isWrong && <CancelOutlinedIcon color="error" fontSize="small" />}
                      <Typography variant="body2">{opt.text}</Typography>
                    </Box>
                  );
                })}
              </Box>
            );
          })}
        </Stack>
        <Button sx={{ mt: 2 }} size="small" variant="outlined" onClick={onDismiss}>
          Dismiss
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Typography variant="h6" fontWeight={600}>Live Quiz</Typography>
        <Chip
          icon={<TimerIcon />}
          label={`${timeLeft}s`}
          color={timerColor as any}
          size="small"
          variant={timeLeft <= 10 ? 'filled' : 'outlined'}
        />
      </Stack>

      <LinearProgress
        variant="determinate"
        value={progress}
        color={timerColor as any}
        sx={{ mb: 2, height: 4, borderRadius: 2 }}
      />

      {error && <Alert severity="error" sx={{ mb: 1 }}>{error}</Alert>}

      <Stack spacing={2}>
        {quiz.questions.map((q, idx) => (
          <Box key={q.id}>
            {idx > 0 && <Divider sx={{ mb: 2 }} />}
            <FormControl component="fieldset" fullWidth>
              <FormLabel component="legend">
                <Typography variant="body2" fontWeight={500}>
                  {idx + 1}. {q.text}
                </Typography>
              </FormLabel>
              <RadioGroup
                value={selectedOptions[q.id] ?? ''}
                onChange={e => handleSelect(q.id, Number(e.target.value))}
              >
                {q.options.map(opt => (
                  <FormControlLabel
                    key={opt.id}
                    value={opt.id}
                    control={<Radio size="small" />}
                    label={<Typography variant="body2">{opt.text}</Typography>}
                  />
                ))}
              </RadioGroup>
            </FormControl>
          </Box>
        ))}
      </Stack>

      <Box sx={{ mt: 2 }}>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={submitting || timeLeft === 0}
        >
          {submitting ? 'Submitting…' : 'Submit Answers'}
        </Button>
      </Box>
    </Box>
  );
}
